import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

/** Optional per-column layout hints, set via `columnDef.meta`. */
export interface ColumnLayout {
  align?: 'right';
  /** Applied to both the header and body cells of the column. */
  className?: string;
}

function cellClass(meta: unknown): string | undefined {
  const layout = meta as ColumnLayout | undefined;
  const cls = [layout?.align === 'right' ? 'text-right' : '', layout?.className]
    .filter(Boolean)
    .join(' ');
  return cls || undefined;
}

function isInteractive(target: EventTarget | null) {
  return (
    target instanceof Element &&
    !!target.closest(
      'button, a, input, select, textarea, label, summary, [contenteditable="true"], [role="button"], [role="link"], [role="checkbox"], [role="combobox"]',
    )
  );
}

interface Props<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  pageSize?: number;
  emptyMessage?: string;
  mobileCards?: boolean;
  onRowClick?: (row: T) => void;
}

export function DataTable<T>({
  columns,
  data,
  pageSize = 20,
  emptyMessage = 'ไม่มีข้อมูล',
  mobileCards = false,
  onRowClick,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });
  return (
    <div
      className={
        mobileCards
          ? 'md:bo-card md:overflow-x-auto'
          : 'bo-card overflow-x-auto'
      }
    >
      <table
        className={`bo-table ${mobileCards ? 'bo-table-mobile-cards' : ''}`}
      >
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  onClick={h.column.getToggleSortingHandler()}
                  className={`cursor-pointer select-none ${cellClass(h.column.columnDef.meta) ?? ''}`}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {{ asc: ' ↑', desc: ' ↓' }[
                    h.column.getIsSorted() as string
                  ] ?? ''}
                </th>
              ))}
              {onRowClick && (
                <th scope="col">
                  <span className="sr-only">รายละเอียด</span>
                </th>
              )}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={Math.max(1, columns.length + (onRowClick ? 1 : 0))}
                className="bo-table-empty text-center text-ink-3 py-10"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={`hover:bg-bg-warm ${onRowClick ? 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink focus-visible:-outline-offset-2' : ''}`}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={
                  onRowClick
                    ? (event) => {
                        if (!isInteractive(event.target))
                          onRowClick(row.original);
                      }
                    : undefined
                }
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (
                          event.target !== event.currentTarget ||
                          event.repeat
                        )
                          return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((c) => {
                  const header = c.column.columnDef.header;
                  const mobileLabel =
                    typeof header === 'string' && header.length > 0
                      ? header
                      : undefined;
                  return (
                    <td
                      key={c.id}
                      data-label={mobileLabel}
                      className={cellClass(c.column.columnDef.meta)}
                    >
                      {flexRender(c.column.columnDef.cell, c.getContext())}
                    </td>
                  );
                })}
                {onRowClick && (
                  <td className="text-right">
                    <span
                      aria-hidden="true"
                      className={
                        mobileCards
                          ? 'hidden md:inline text-xl text-ink-3'
                          : 'text-xl text-ink-3'
                      }
                    >
                      ›
                    </span>
                    {mobileCards && (
                      <button
                        type="button"
                        className="md:hidden min-h-11 px-1 text-sm-label text-primary hover:underline"
                        onClick={() => onRowClick(row.original)}
                      >
                        ดูรายละเอียด
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
